import { LIST_VIEW_RETURN_KEY } from '@/lib/listViewState';
import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';

import { PlaceholderCard } from './PlaceholderCard';
import { useDocumentTitle } from './useDocumentTitle';

// `/ground-truth`'s `validateSearch` doesn't allow-list the return marker, so it never appears
// in that route's typed search (`useListViewState` reads it off the raw URL). Cast once, here.
export const RETURN_TO_GROUND_TRUTH_SEARCH = {
    [LIST_VIEW_RETURN_KEY]: true,
} as unknown as { q?: string };

/** Link back to the Ground Truth list, restoring its filters. */
export function GroundTruthBackLink() {
    return (
        <Button asChild variant="outline" size="sm" className="mt-4">
            <Link to="/ground-truth" hash="table" search={RETURN_TO_GROUND_TRUTH_SEARCH}>
                Back to Ground Truth
            </Link>
        </Button>
    );
}

interface GroundTruthNotFoundProps {
    /** Names what was missing, e.g. "File #42". */
    what?: string;
    'data-testid'?: string;
}

/** Tab title for a missing page; pages that own the title pass the same string so theirs doesn't override it. */
export const notFoundTitle = (what = 'This page') =>
    what === 'This page' ? 'Page not found' : `${what} not found`;

/** "Not found" message with a link back to the list; also the route-level `notFoundComponent`. */
export function GroundTruthNotFound({
    what = 'This page',
    'data-testid': dataTestId = 'GroundTruthNotFound',
}: GroundTruthNotFoundProps) {
    useDocumentTitle(notFoundTitle(what));
    return (
        <main className="flex h-screen items-center justify-center bg-white">
            <PlaceholderCard
                className="rounded-md border border-dashed bg-white p-8 text-center"
                data-testid={dataTestId}
                title={`${what} was not found.`}
                titleClassName="text-sm font-medium text-gray-900"
                description="It may have been deleted, or the address may be mistyped."
                descriptionClassName="text-xs text-muted-foreground mt-1 max-w-md mx-auto"
            >
                <GroundTruthBackLink />
            </PlaceholderCard>
        </main>
    );
}

/** Route-level `errorComponent`: unexpected errors stay inside the app layout with a way back. */
function GroundTruthRouteError() {
    return (
        <div className="flex h-screen items-center justify-center bg-white">
            <PlaceholderCard
                className="rounded-md border border-dashed bg-white p-8 text-center"
                data-testid="GroundTruthRouteError"
                title="Something went wrong."
                titleClassName="text-sm font-medium text-gray-900"
                description="This page could not be shown. Go back to the Ground Truth list and try again."
                descriptionClassName="text-xs text-muted-foreground mt-1 max-w-md mx-auto"
            >
                <GroundTruthBackLink />
            </PlaceholderCard>
        </div>
    );
}

/** Spread into every `/ground-truth` route so bad addresses and render errors keep the app layout. */
export const groundTruthRouteBoundaries = {
    notFoundComponent: () => <GroundTruthNotFound />,
    errorComponent: GroundTruthRouteError,
};
